import { useState, useMemo, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Tab,
  IconButton,
  Tooltip,
  Tabs,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from "@mui/icons-material/Category";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderClientes from "components/_refactor/headers/clients";
import FilterCard from "components/_refactor/filter-card";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import IndicatorCards from "./components/indicator-cards";
import ContentTable from "./components/content-table";
import DialogNovoConteudo from "./components/dialog-novo-conteudo";
import DialogCampanha from "./components/dialog-campanha";
import DialogBanner from "./components/dialog-banner";
import DialogMaterial from "./components/dialog-material";
import DialogVideo from "./components/dialog-video";
import DialogCategorias from "./components/dialog-categorias";
import DialogVisualizar from "./components/dialog-visualizar";
import DialogHistorico from "./components/dialog-historico";
import DialogPlaylists from "./components/dialog-playlists";
import {
  CONTENT_TABS,
  CONTENT_TYPES,
  CONTENT_STATUS,
  SORT_OPTIONS,
  matchesTab,
} from "./constants";
import {
  getUnifiedList,
  getStatusSummary,
  listCategories,
  changeCampaignStatus,
  changeContentStatus,
  deleteCampaign,
  deleteContent,
  duplicateContent,
  shareCampaign,
  shareContent,
  loadFullEntity,
} from "./services/franchise-marketing-api";

const DIALOG_MAP = {
  Campanha: "campanha",
  Banner: "banner",
  Lookbook: "material",
  Video: "video",
  MaterialPdf: "material",
  Institucional: "material",
  VmMerchandising: "material",
  Material: "material",
  Categorias: "categorias",
  Playlists: "playlists",
};

const INITIAL_COUNTS = {
  Publicados: 0,
  Rascunhos: 0,
  Agendados: 0,
  Expirados: 0,
  Arquivados: 0,
  EmRevisao: 0,
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];

export default function GestaoMarketing() {
  const snackbar = useSnackbar();

  const [contents, setContents] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [counts, setCounts] = useState(INITIAL_COUNTS);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0]);
  const [filter, setFilter] = useState({
    search: "",
    tipo: null,
    categoria: null,
    status: null,
  });
  const [searching, setSearching] = useState(false);
  const [loadingEntity, setLoadingEntity] = useState(false);
  const [deletingRowKey, setDeletingRowKey] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [openNovoConteudo, setOpenNovoConteudo] = useState(false);
  const [activeDialog, setActiveDialog] = useState(null);
  const [contentToEdit, setContentToEdit] = useState(null);
  const [materialPreset, setMaterialPreset] = useState(null);

  const [viewEntity, setViewEntity] = useState(null);
  const [viewIsCampaign, setViewIsCampaign] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);

  const loadCategories = async () => {
    try {
      const categories = await listCategories(false);
      setCategoryOptions(
        categories.map((category) => ({
          value: category.IdFranquiaMarketingCategoria,
          label: category.Nome,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const loadData = async () => {
    setSearching(true);

    try {
      const searchTerm = (globalSearch || filter.search || "").trim();
      const status = statusFilter || filter.status?.value || null;

      const [listResult, summary] = await Promise.all([
        getUnifiedList({
          aba: activeTab,
          status,
          busca: searchTerm,
          ordenacao: sortBy?.value || "recentes",
          pagina: page,
          tamanho: pageSize,
        }),
        getStatusSummary(),
      ]);

      setContents(
        (listResult.Itens || []).map((item) => ({
          ...item,
          rowKey: `${item.ItemTipo}-${item.Id}`,
        })),
      );
      setTotalItems(listResult.Total || 0);
      setCounts({ ...INITIAL_COUNTS, ...summary });
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao carregar conteúdos. Tente novamente.",
        { severity: "error" },
      );
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadData();
  }, [activeTab, statusFilter, sortBy, refreshTrigger, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFilter, sortBy, refreshTrigger]);

  const filteredData = useMemo(() => {
    let result = [...contents];

    result = result.filter((item) => matchesTab(item.Tipo, activeTab));

    if (filter.tipo?.value) {
      result = result.filter((item) => item.Tipo === filter.tipo.value);
    }

    if (filter.categoria?.label) {
      result = result.filter(
        (item) => item.Categoria === filter.categoria.label,
      );
    }

    if (filter.status?.value && !statusFilter) {
      result = result.filter((item) => item.Status === filter.status.value);
    }

    return result;
  }, [contents, activeTab, filter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const handleSearch = () => {
    setPage(1);
    setRefreshTrigger((value) => value + 1);
  };

  const handleClearFilters = () => {
    setFilter({ search: "", tipo: null, categoria: null, status: null });
    setStatusFilter(null);
    setGlobalSearch("");
    setPage(1);
    setRefreshTrigger((value) => value + 1);
  };

  const openDialog = (type, content = null, preset = null) => {
    setContentToEdit(content);
    setMaterialPreset(preset);
    setActiveDialog(type);
    setOpenNovoConteudo(false);
  };

  const openDialogWithEntity = async (type, row, preset = null) => {
    setLoadingEntity(true);
    try {
      const full = await loadFullEntity(row);
      openDialog(type, full || row, preset);
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao carregar dados para edição.",
        { severity: "error" },
      );
    } finally {
      setLoadingEntity(false);
    }
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setContentToEdit(null);
    setMaterialPreset(null);
  };

  const handleDialogSaved = () => {
    closeDialog();
    loadData();
    loadCategories();
  };

  const handleNovoConteudoSelect = (type) => {
    const dialogType = DIALOG_MAP[type];
    if (type === "Lookbook") {
      openDialog("material", null, "Lookbook");
    } else if (dialogType) {
      openDialog(dialogType);
    }
  };

  const isCampaign = (row) => row.ItemTipo === "Campanha";

  const handleTableAction = async (action, row) => {
    if (action === "editar") {
      const dialogType = DIALOG_MAP[row.Tipo] || DIALOG_MAP.Material;
      const preset = row.Tipo === "Lookbook" ? "Lookbook" : null;
      await openDialogWithEntity(dialogType, row, preset);
      return;
    }

    if (action === "visualizar") {
      setLoadingEntity(true);
      try {
        const full = await loadFullEntity(row);
        setViewEntity(full || row);
        setViewIsCampaign(isCampaign(row));
      } catch (error) {
        console.error(error);
        snackbar.showMessage(error?.message || "Falha ao carregar conteúdo.", {
          severity: "error",
        });
      } finally {
        setLoadingEntity(false);
      }
      return;
    }

    if (action === "historico") {
      setHistoryTarget(row);
      return;
    }

    if (action === "excluir") {
      setDeletingRowKey(row.rowKey);
      let deleted = false;
      try {
        if (isCampaign(row)) {
          await deleteCampaign(row.Id);
        } else {
          await deleteContent(row.Id);
        }
        snackbar.showMessage(`"${row.Titulo}" excluído`, {
          severity: "warning",
        });
        deleted = true;
      } catch (error) {
        console.error(error);
        snackbar.showMessage(
          error?.message || "Falha ao excluir conteúdo. Tente novamente.",
          { severity: "error" },
        );
      } finally {
        setDeletingRowKey(null);
      }
      if (deleted) {
        await loadData();
      }
      return;
    }

    try {
      if (action === "publicar") {
        if (isCampaign(row)) {
          await changeCampaignStatus(row.Id, "Publicado");
        } else {
          await changeContentStatus(row.Id, "Publicado");
        }
        snackbar.showMessage(`"${row.Titulo}" publicado`, {
          severity: "success",
        });
      } else if (action === "despublicar") {
        if (isCampaign(row)) {
          await changeCampaignStatus(row.Id, "Rascunho");
        } else {
          await changeContentStatus(row.Id, "Rascunho");
        }
        snackbar.showMessage(`"${row.Titulo}" despublicado`, {
          severity: "success",
        });
      } else if (action === "arquivar") {
        if (isCampaign(row)) {
          await changeCampaignStatus(row.Id, "Arquivado");
        } else {
          await changeContentStatus(row.Id, "Arquivado");
        }
        snackbar.showMessage(`"${row.Titulo}" arquivado`, {
          severity: "success",
        });
      } else if (action === "duplicar") {
        if (isCampaign(row)) {
          snackbar.showMessage("Duplicação disponível apenas para conteúdos.", {
            severity: "info",
          });
          return;
        }
        await duplicateContent(row.Id);
        snackbar.showMessage(`Cópia de "${row.Titulo}" criada`, {
          severity: "success",
        });
      } else if (action === "compartilhar") {
        const result = isCampaign(row)
          ? await shareCampaign(row.Id)
          : await shareContent(row.Id);
        const token = result?.TokenCompartilhamento;
        if (token) {
          await navigator.clipboard.writeText(token);
        }
        snackbar.showMessage(`Link de "${row.Titulo}" copiado`, {
          severity: "success",
        });
      } else {
        snackbar.showMessage("Ação executada", { severity: "success" });
        return;
      }

      loadData();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao executar a ação. Tente novamente.",
        { severity: "error" },
      );
    }
  };

  return (
    <>
      <HeaderClientes
        section="Gestão de Marketing"
        subsection="Marketing"
        tooltipMessage="Gerencie campanhas, banners, materiais, lookbooks e vídeos para as franquias."
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card style={{ marginBottom: 16, borderRadius: 12 }}>
          <CardContent>
            <Typography
              variant="body2"
              style={{
                color: themeColorsDesingSystem.gray.dark,
                marginBottom: 16,
              }}
            >
              Centralize e publique conteúdos oficiais da marca para as
              franquias.
            </Typography>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={4}>
                <InputText
                  label="Buscar conteúdos..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <AutoCompleteField
                  label="Ordenação"
                  value={sortBy}
                  onChange={setSortBy}
                  options={SORT_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  justifyContent="flex-end"
                  alignItems="center"
                  style={{ gap: 8 }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PlaylistPlayIcon />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => openDialog("playlists")}
                  >
                    Playlists
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<CategoryIcon />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => openDialog("categorias")}
                  >
                    Categorias
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setOpenNovoConteudo(true)}
                  >
                    Novo Conteúdo
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box marginBottom={3}>
          <IndicatorCards
            counts={counts}
            activeStatus={statusFilter}
            onStatusClick={setStatusFilter}
          />
        </Box>

        <Card
          style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden" }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            style={{ backgroundColor: themeColorsDesingSystem.background.main }}
          >
            {CONTENT_TABS.map((tab) => (
              <Tab
                key={tab.key}
                label={tab.label}
                value={tab.key}
                style={{
                  textTransform: "none",
                  fontWeight: activeTab === tab.key ? 600 : 400,
                  color: themeColorsDesingSystem.text.primary,
                  minWidth: 100,
                }}
              />
            ))}
          </Tabs>
        </Card>

        <FilterCard
          handleSearch={handleSearch}
          searching={searching || loadingEntity}
          disabled={searching || loadingEntity}
          title="Filtros"
        >
          <Grid item xs={12} lg={4}>
            <InputText
              label="Buscar por título"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              disabled={searching}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <AutoCompleteField
              label="Tipo"
              value={filter.tipo}
              onChange={(v) => setFilter({ ...filter, tipo: v })}
              options={CONTENT_TYPES}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} lg={3}>
            <AutoCompleteField
              label="Categoria"
              value={filter.categoria}
              onChange={(v) => setFilter({ ...filter, categoria: v })}
              options={categoryOptions}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <AutoCompleteField
              label="Status"
              value={filter.status}
              onChange={(v) => setFilter({ ...filter, status: v })}
              options={CONTENT_STATUS}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={searching}
            />
          </Grid>
        </FilterCard>

        <Card style={{ borderRadius: 12, marginTop: 16 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={2}
              flexDirection="row"
            >
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <Typography
                  variant="h3"
                  style={{ color: themeColorsDesingSystem.text.primary }}
                >
                  Conteúdos
                </Typography>
                <Tooltip title="Atualizar">
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    aria-label="Atualizar"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  {totalItems} resultado(s)
                </Typography>
              </Box>
              <Button
                variant="text"
                color="primary"
                startIcon={<TuneIcon />}
                onClick={handleClearFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                size="small"
              >
                Limpar filtros
              </Button>
            </Box>
            <Box position="relative">
              {deletingRowKey && !searching && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.75)",
                    zIndex: 10,
                    borderRadius: 8,
                    minHeight: 120,
                  }}
                >
                  <LoadingData
                    searching
                    label="Excluindo conteúdo..."
                    hiddenIcon
                  />
                </Box>
              )}
              <ContentTable
                data={filteredData}
                loading={searching || loadingEntity}
                onAction={handleTableAction}
                serverPagination
                deletingRowKey={deletingRowKey}
              />
            </Box>
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginTop={2}
                flexWrap="wrap"
                style={{ gap: 12 }}
              >
                <AutoCompleteField
                  label="Itens por página"
                  value={PAGE_SIZE_OPTIONS.map((v) => ({
                    value: v,
                    label: String(v),
                  })).find((o) => o.value === pageSize)}
                  onChange={(v) => {
                    setPageSize(v?.value || 25);
                    setPage(1);
                  }}
                  options={PAGE_SIZE_OPTIONS.map((v) => ({
                    value: v,
                    label: String(v),
                  }))}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>

      <DialogNovoConteudo
        open={openNovoConteudo}
        onClose={() => setOpenNovoConteudo(false)}
        onSelect={handleNovoConteudoSelect}
      />
      <DialogCampanha
        open={activeDialog === "campanha"}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
        content={contentToEdit}
        categoryOptions={categoryOptions}
      />
      <DialogBanner
        open={activeDialog === "banner"}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
        content={contentToEdit}
        categoryOptions={categoryOptions}
      />
      <DialogMaterial
        open={activeDialog === "material"}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
        content={contentToEdit}
        presetTipo={materialPreset}
        categoryOptions={categoryOptions}
      />
      <DialogVideo
        open={activeDialog === "video"}
        onClose={closeDialog}
        onSaved={handleDialogSaved}
        content={contentToEdit}
        categoryOptions={categoryOptions}
      />
      <DialogCategorias
        open={activeDialog === "categorias"}
        onClose={closeDialog}
        onSaved={loadCategories}
      />
      <DialogPlaylists
        open={activeDialog === "playlists"}
        onClose={closeDialog}
      />
      <DialogVisualizar
        open={!!viewEntity}
        onClose={() => setViewEntity(null)}
        entity={viewEntity}
        isCampaign={viewIsCampaign}
      />
      <DialogHistorico
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        target={historyTarget}
      />
    </>
  );
}
